import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

export const sanityClient = createClient({
  projectId: '762y97p0', // Your Sanity project ID
  dataset: 'production', // Your dataset
  useCdn: false, // Disable CDN for production to ensure fresh content
  apiVersion: '2023-05-03', // Use current date
  perspective: 'published', // Use published perspective
});

// Image URL builder for optimized images
const builder = imageUrlBuilder(sanityClient);

/**
 * Generate optimized image URL from Sanity image asset
 * @param {Object} source - Sanity image object
 * @param {number} width - Desired width (optional)
 * @param {number} height - Desired height (optional)
 * @param {number} quality - Image quality 1-100 (default: 80)
 * @returns {string} Optimized image URL
 */
export function urlFor(source) {
  return builder.image(source);
}

/**
 * Get optimized image URL with specific dimensions
 * Usage: getOptimizedImageUrl(image, { width: 800, quality: 85 })
 */
export function getOptimizedImageUrl(source, options = {}) {
  if (!source) return '';
  
  const {
    width,
    height,
    quality = 80,
    format = 'auto', // auto, webp, jpg, png
    fit = 'max', // max, crop, fill, min
  } = options;

  let url = builder.image(source).auto('format').quality(quality);
  
  if (width) url = url.width(width);
  if (height) url = url.height(height);
  if (fit) url = url.fit(fit);
  
  return url.url();
}

// Helper functions to fetch different content types
export async function getTimelineData() {
  const query = `*[_type == "portfolioItem" && showInTimeline == true] | order(timelineOrder asc) {
    _id,
    title,
    showTitleInTimeline,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    secondImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    timelineOrder
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getTeamMembers() {
  const query = `*[_type == "teamMember"] | order(order asc) {
    _id,
    name,
    jobTitle,
    description,
    picture {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    order
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getNewsArticles() {
  const query = `*[_type == "newsArticle"] | order(publishedDate desc) {
    _id,
    titleEn,
    titleIt,
    "slug": slug.current,
    publishedDate,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getRecentNewsArticles(limit = 3) {
  const query = `*[_type == "newsArticle"] | order(publishedDate desc)[0...${limit}] {
    _id,
    titleEn,
    titleIt,
    "slug": slug.current,
    publishedDate,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getNewsArticleBySlug(slug) {
  const query = `*[_type == "newsArticle" && slug.current == $slug][0] {
    _id,
    titleEn,
    titleIt,
    "slug": slug.current,
    publishedDate,
    contentEn,
    contentIt,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    additionalImages[] {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query, { slug });
}

export async function getPortfolioItems() {
  const query = `*[_type == "portfolioItem"] | order(order asc) {
    _id,
    title,
    externalUrl,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    secondImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    order
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getPortfolioItemBySlug(slug) {
  const query = `*[_type == "project" && slug.current == $slug][0] {
    _id,
    titleIt,
    titleEn,
    "slug": slug.current,
    publishedDate,
    contentIt,
    contentEn,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    secondImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    additionalImages[] {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query, { slug });
}

export async function getPartnerLogos() {
  const query = `*[_type == "partnerLogo"] | order(order asc) {
    _id,
    name,
    logo {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    url,
    order
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getFeaturedInstagramPosts() {
  const query = `*[_type == "featuredInstagramPost"] | order(order asc) {
    _id,
    image {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    instagramUrl,
    caption,
    order
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getCollaborators() {
  const query = `*[_type == "collaborator"] | order(order asc) {
    _id,
    name,
    logo {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    url,
    order
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getPortfolioItemsWithContent() {
  const query = `*[_type == "project"] | order(publishedDate desc) {
    _id,
    titleIt,
    titleEn,
    "slug": slug.current,
    publishedDate,
    mainImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    },
    secondImage {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getWebsiteTexts(category) {
  const query = `*[_type == "webTexts" && category == $category] {
    _id,
    key,
    textIt,
    textEn,
    description
  }`;
  
  return await sanityClient.fetch(query, { category });
}

export async function getAllWebsiteTexts() {
  const query = `*[_type == "webTexts"] {
    _id,
    category,
    key,
    textIt,
    textEn,
    description
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getServices() {
  const query = `*[_type == "service"] | order(order asc) {
    _id,
    order,
    titleIt,
    titleEn,
    descriptionIt,
    descriptionEn
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getFAQs() {
  const query = `*[_type == "faq"] | order(order asc) {
    _id,
    order,
    questionIt,
    questionEn,
    answerIt,
    answerEn
  }`;
  
  return await sanityClient.fetch(query);
}

export async function getFounders() {
  const query = `*[_type == "founder"] | order(order asc) {
    _id,
    order,
    nameIt,
    nameEn,
    descriptionIt,
    descriptionEn,
    picture {
      asset-> {
        url,
        metadata {
          dimensions
        }
      }
    }
  }`;
  
  return await sanityClient.fetch(query);
}
